import { Injectable } from '@angular/core';
import { RouterService, User } from '@mitel/cloudlink-sdk';
import { Subject, Subscription } from 'rxjs';
import { AppInjector } from '../../app-injector';
import { Mitel5000ConfigurationComponent } from '../../modules/users-single-edit/components/product-configs/mitel-5000-config/mitel-5000-config.component';
import { mivoice5000Licenses } from '../constants/constants';
import { ProductLicense, ProductLicenseItemServiceInf } from '../interfaces/product-licenses-interfaces';
import { AdminService } from './admin.service';
import { IntegPolicyService } from './integ-policy.service';
import { IntegSharedCCService } from './integ-shared-cc.service';
import { OrdersService } from './orders.service';
import { SpinnerService } from './spinner.service';
import { RBACAuthGuardService } from '../guards/rbac-auth-guard.service';
import { ActionType, CategoryId } from '../utils/rbac.util';
import { CustomValidators } from '../utils/CustomValidators';

@Injectable({
  providedIn: 'root'
})
export class UserProdMitel5000Service implements ProductLicenseItemServiceInf { 
  private adminService: AdminService;
  private ordersService: OrdersService;
  private policyService: IntegPolicyService;
  private spinnerService: SpinnerService;
  private routerSvc: RouterService;
  private ccSharedService: IntegSharedCCService;
  private rbacAuthGuardService: RBACAuthGuardService;

  subscription: Subscription; 
  public updateProdNLcnsEvent = new Subject<any>();
  constructor() { }

  initializeServices()
	{
   
    if ( !this.adminService )
      this.adminService = AppInjector.injector.get(AdminService);

    if ( !this.ordersService)
      this.ordersService = AppInjector.injector.get(OrdersService);
    
    if ( !this.policyService )
			this.policyService = AppInjector.injector.get(IntegPolicyService);

    if ( !this.spinnerService )
			this.spinnerService = AppInjector.injector.get(SpinnerService);

    if ( !this.routerSvc )
			this.routerSvc = AppInjector.injector.get(RouterService);
    
    if( !this.ccSharedService)
      this.ccSharedService = AppInjector.injector.get(IntegSharedCCService);

    if(!this.subscription) {
      this.subscription = new Subscription();
    }
    
    if (!this.rbacAuthGuardService )
      this.rbacAuthGuardService = AppInjector.injector.get(RBACAuthGuardService);

	}

  canBeAdded(user: User, product: ProductLicense) {
    this.initializeServices();
    let promises = [];

    if (!user.userVerified) {
      if (user.email && user.email.length > 0) {
        let dupEmailPromise = this.adminService.userHasADuplicateEmail(user.accountId, user).then(hasDup => {
          const validEmail = CustomValidators.userHasValidEmail(user);
          return !hasDup && validEmail;
        });
        promises.push(dupEmailPromise);
      } else {
        // if user is verified but doesn't have an email then we allow it to get a license
        // User from MiCloud Connect are verified but they may have invalid email or no email. Email is not validated. 
        product.statusString = 'users_edit_page.user_not_eligible';
        return Promise.resolve(false);
      }
    } else {
      promises.push(Promise.resolve(true));
    }

    let remainingLicense = this.getRemainingLicenses(user).then( value => {
      return value > 0;
    });
    promises.push(remainingLicense);

    return Promise.all(promises).then( result => {
      if (result[0] && result[1])
          return true;
      else if (!result[1])
        product.statusString = 'users_edit_page.licensesNotAvailable';
      else if (!result[0])
        product.statusString = 'users_edit_page.user_not_eligible';

      return false;
    });
  }

  hasRequiredAccountIntegrations( user: User, accountIntegrations: string[] ):  Promise<boolean>
  {
    return Promise.resolve( accountIntegrations.includes('MiVoice5000') );
  }

  getConfigurationDialog(setupComplete?: boolean): Promise<any> {
    return Promise.resolve(Mitel5000ConfigurationComponent);
  }

  isSetupComplete(user: User): Promise<boolean> {
    this.initializeServices();
    return this.ordersService.getUserLicenses(user.userId)
              .then((userLicenses) => {
                const result = userLicenses.find( element => {
                  return element.enable && mivoice5000Licenses.includes(element.name);
                });
                return !!result;
              })
              .catch( (err) => {
                console.log('Failed to get user Licenses', err);
                return false;
              });
  }

  handleProductStateChange(user: User, state: boolean, isSubComponent?: boolean): Promise<any> {
    console.log('handling state change of mitel 5000 product for the user');
    this.initializeServices();
    return this.adminService.getUserWithoutFrills(user.userId, 'tags').then( userWithTags => {
      let tagBody = {
        products: []
      };
      if (userWithTags.tags?.products) {
        tagBody.products.push(...userWithTags.tags.products);
      }

      if(state) {

        //Adding Mitel 5000 Product to the user.
        if(tagBody.products.indexOf('mivoice5000') < 0) {
          tagBody.products.push('mivoice5000');
        }
        return this.adminService.tryUpdateTagsOfUserByAccountId(user.accountId, user.userId, tagBody, userWithTags.tags? userWithTags.tags: {});

      } else {

        // removing Mitel 5000 product from the user
        let ndx =  tagBody.products.indexOf('mivoice5000');
        if(ndx > -1)
          tagBody.products.splice(ndx, 1);
        return this.ordersService.getUserLicenses(user.userId).then( licenses => {
          const licenseToRemove = licenses.find(license => {
            return license.enable && mivoice5000Licenses.includes(license.name);
          });
          if(licenseToRemove) {
            // Found the license to remove
            return this.ordersService.addOrRemoveUserLicenseWithoutErrorHandler(userWithTags.userId, licenseToRemove.name, false).then( result => {
                  this.ordersService.clearAcctSubscriptionsCache();
                  console.log(" user entitlement is removed successfully", result);
                  return this.adminService.tryUpdateTagsOfUserByAccountId(user.accountId, user.userId, tagBody, userWithTags.tags? userWithTags.tags: {});
                })
          } else {
            // for the scenario where product is removed before setup is completed.
            return this.adminService.tryUpdateTagsOfUserByAccountId(user.accountId, user.userId, tagBody, userWithTags.tags? userWithTags.tags: {});
          }
        });
      }
    });
  }

  isButtonDisabled(user: User, product: ProductLicense): Promise<boolean> {
    this.initializeServices();

    let isDenied = this.rbacAuthGuardService.isPermissionDeniedForUser(CategoryId.USERSLICENSE, ActionType.DELETE);
    return Promise.resolve(isDenied);
  }

  isProductEnabled(user: User, isSubComponent?): Promise<boolean> { 
    this.initializeServices();
    let promises = [];
    const mitel5000licensePromise = this.isSetupComplete(user);
    promises.push(mitel5000licensePromise);
    const userTagPromise = this.adminService.getUserTagsByAccountIdWithoutSpinner(user.accountId, user.userId).then( tags => {
                              if (tags.products && tags.products.length > 0 && tags.products.indexOf('mivoice5000') > -1) {
                                return true;
                              } else {
                                return false;
                              }
                            });
    promises.push(userTagPromise);
    return Promise.all(promises).then( values => {
      if(values[0] || values[1]) {
        return true;
      }
      return false;
    })
  }

  getRemainingLicenses(user: User, isSubComponent?: boolean): Promise<number> {

    this.initializeServices();
    return this.ordersService.getRemainingLicenses().then( remainingLicenses => {
      let totalLicenses = 0;
      for( let license in remainingLicenses) {
        if (mivoice5000Licenses.includes(license)) {
          totalLicenses += remainingLicenses[license];
        }
      }
      return totalLicenses;
    }).catch(error => {
      console.log("Failed to get remaining mitel 5000 licenses", error);
      return 0;
    });
  }

  getSubComponentConfigurationDetails ( user: User, product?: ProductLicense ): Promise<any>
  {
    let details: any = {
      subComponentNames: ["meet", "chat"],
      subComponentLabel: "product_licenses.features",
      subComponent: [],
      subComponentEditable: false
    }
    return Promise.resolve( details );
  }

  getSubComponent(user: User, product: ProductLicense): Promise<any> { 
    return Promise.resolve(product.subComponentNames);
  }

  getUpdateProdNLcnsSecEventFromConfigComp() {
    return this.updateProdNLcnsEvent;
  }

  getDisplayName(user: User, isSetupComplete: boolean) {
    this.initializeServices();
    if(isSetupComplete) {
      return this.ordersService.getUserLicenses(user.userId).then( licenses => {
        const licenseAdded = licenses.find(license => {
          return license.enable && mivoice5000Licenses.includes(license.name);
        });
        if(licenseAdded) {
          return "product_licenses.mivoice5000." + licenseAdded.name;
        } else {
          return "product_licenses.mivoice5000.MiVoice5000_NoLicense";
        }
      }).catch(err => {
        console.log("Failed to get user licenses to display appropriate name for mitel 5000");
        return "product_licenses.mivoice5000.name";
      });
    }
    return Promise.resolve("product_licenses.mivoice5000.MiVoice5000_NoLicense");
  }

  showTableOfContents(user: User): Promise<boolean>
  {
    return Promise.resolve(false);
  }

  destroySubscriptions() {
    if(this.subscription){
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
  
}
